import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { JobService } from "@/services/jobService";
import { Error } from "@/components/shared/Error";
import JobSkeleton from "./component/JobSkeleton";
import JobCard from "./component/JobCard";
import ListPagination from "@/components/shared/ListPagination";
import { useDebounce } from "@/hooks/useDebounce";
import useLiveResource from "@/hooks/useLiveResource";
import { getTenantSlug } from "@/utils/tenantSlug";
import { Button } from "@/components/ui/Button";

const JobListing = () => {
  const [jobList, setJobList] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalUnavailable, setPortalUnavailable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0);

  const debouncedSearch = useDebounce(search);

  // Stable ref so WS handlers always read latest params without re-subscribing
  const fetchParamsRef = useRef({ currentPage, pageSize, debouncedSearch });
  useEffect(() => {
    fetchParamsRef.current = { currentPage, pageSize, debouncedSearch };
  }, [currentPage, pageSize, debouncedSearch]);

  // ─── FETCH ───
  const fetchJobList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const { currentPage, pageSize, debouncedSearch } = fetchParamsRef.current;
      const data = await JobService.fetJobList({
        page: currentPage,
        perPage: pageSize,
        search: debouncedSearch,
      });
      setJobList(data.data.jobs ?? []);
      setTotalJobs(data.data.pagination?.total ?? 0);
      setPortalUnavailable(false);
    } catch (e) {
      console.error("Error fetching jobs", e.message);
      // Tenant deactivated/deleted — backend blocks the whole public portal
      // with this error_code (see server/app/core/tenant_db.py get_public_db).
      if (e?.response?.data?.error_code === "PORTAL_UNAVAILABLE") {
        setPortalUnavailable(true);
        setJobList([]);
      } else if (!silent) {
        setError(e.message ?? "Failed to load jobs.");
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // ─── EFFECTS ──────
  useEffect(() => {
    fetchJobList();
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchJobList({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchJobList]);

  // ─── WEBSOCKET ─────

  // One subscription for all six job events. Every one of them is a refetch:
  // the envelope carries the job's id and nothing else, so there is no status
  // to patch a row with — and the list this visitor is looking at is filtered,
  // paged and searched, which only the server can reproduce.
  //
  // The public transport is the point: a signed-out visitor has no ticket, and
  // this page is mostly read by signed-out visitors.
  useLiveResource({
    keys: "job",
    transport: "public",
    slug: getTenantSlug(),
    onChange: () => fetchJobList({ silent: true }),
  });

  // ─── RENDER ───
  if (portalUnavailable) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-20 font-sans text-center" style={{ zoom: 0.95 }}>
        <svg className="w-10 h-10 text-text-secondary mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        <h1 className="text-text-primary font-bold text-xl leading-tight mb-1">Career portal unavailable</h1>
        <p className="text-text-secondary text-sm">This career portal is currently unavailable. Please check back later.</p>
      </div>
    );
  }

  return (
<div className="flex-1 px-4 sm:px-6 md:px-8 py-5 sm:py-7 font-sans" style={{ zoom: 0.95 }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-5">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">Available Jobs</h1>
          {/* <p className="text-text-secondary text-xs mt-0.5">{isLoading ? "Loading positions…" : `${totalJobs} Position${totalJobs !== 1 ? "s" : ""} Found`}</p> */}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-primary" />
          <Input
            type="text"
            placeholder="Search by Title, Role Overview, Job ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-72 bg-background-secondary text-text-primary text-sm rounded-3xl pl-9 pr-9 py-2 border-none"
          />
          {search && (
            <Button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-secondary hover:text-text-primary">
              <X size={16} />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-5 md:gap-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <JobSkeleton key={i} />)
        ) : error ? (
          <Error error={error} onHandleRetry={fetchJobList} />
        ) : jobList.length === 0 ? (
          <p className="text-gray-500 col-span-2 text-center py-12 text-[22px]">
            {search ? `No positions found matching "${search}"` : "No open positions at this time."}
          </p>
        ) : (
          jobList.map((job) => <JobCard key={job.id ?? job.job_id} job={job} />)
        )}
      </div>

      {jobList.length > 0 && (
        <ListPagination
          page={currentPage}
          pageSize={pageSize}
          total={totalJobs}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          label="jobs"
          pageSizeOptions={[5, 10, 25, 50]}
          className="mt-4"
        />
      )}
    </div>
  );
};

export default JobListing;
